import React, { useRef, useEffect, useState } from "react";
import {
  Animated,
  PanResponder,
  Dimensions,
  StyleSheet,
  Modal,
  View,
  BackHandler,
} from "react-native";
import { ScrollView } from ".";
import { SCREEN_WIDTH, TABLET_PC } from "../libs/utils/constants";
import useOrientation from "../libs/hooks/useOrientation";
import { Orientation } from "expo-screen-orientation";

const { height: SCREEN_HEIGHT } = Dimensions.get("window");

const AnimatedBottomSheet = ({
  isVisible,
  children,
  onBackdropPress,
  scrollViewProps,
}: {
  isVisible: boolean;
  children: React.ReactElement;
  onBackdropPress: () => void;
  scrollViewProps?: object;
}) => {
  const orientation = useOrientation();
  const [modalOpen, setModalOpen] = useState(isVisible);
  const bottomSheetHeight = SCREEN_HEIGHT * 0.5;
  const bottomSheetTranslateY = useRef(
    new Animated.Value(SCREEN_HEIGHT)
  ).current;

  let backHandler = null;

  const opacityOverlay = useRef(new Animated.Value(0)).current;

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, gestureState) => gestureState.dy > 0,
      onPanResponderMove: (_, gestureState) => {
        if (gestureState.dy >= 0) {
          bottomSheetTranslateY.setValue(gestureState.dy);
        }
      },
      onPanResponderRelease: (_, gestureState) => {
        if (gestureState.dy > bottomSheetHeight / 2) {
          closeBottomSheet();
        } else {
          resetBottomSheetPosition();
        }
      },
    })
  ).current;

  useEffect(() => {
    if (isVisible) {
      openBottomSheet();
    } else {
      closeBottomSheet();
    }
  }, [isVisible]);

  const openBottomSheet = () => {
    setModalOpen(true);
    Animated.parallel([
      Animated.spring(bottomSheetTranslateY, {
        toValue: 0,
        useNativeDriver: true,
        bounciness: 0,
      }),
      Animated.spring(opacityOverlay, {
        toValue: 1,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const closeBottomSheet = () => {
    Animated.parallel([
      Animated.timing(bottomSheetTranslateY, {
        toValue: SCREEN_HEIGHT,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(opacityOverlay, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setModalOpen(false);
      onBackdropPress();
    });
  };

  const resetBottomSheetPosition = () => {
    Animated.spring(bottomSheetTranslateY, {
      toValue: 0,
      useNativeDriver: true,
    }).start();
  };

  const height =
    orientation === Orientation.LANDSCAPE_LEFT ||
    orientation === Orientation.LANDSCAPE_RIGHT
      ? SCREEN_WIDTH
      : SCREEN_HEIGHT;

  return (
    <Modal visible={modalOpen} transparent onRequestClose={closeBottomSheet}>
      <Animated.View
        style={[styles.overlay, { opacity: opacityOverlay }]}
        pointerEvents={isVisible ? "auto" : "none"}
      />
      <Animated.View
        style={[
          styles.bottomSheet,
          {
            maxHeight: height,
            transform: [{ translateY: bottomSheetTranslateY }],
          },
        ]}
        {...panResponder.panHandlers}
      >
        <ScrollView contentContainerStyle={{ flex: 1 }} {...scrollViewProps}>
          {children}
        </ScrollView>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  bottomSheet: {
    position: "absolute",
    left: TABLET_PC ? 10 : 0,
    right: TABLET_PC ? 10 : 0,
    bottom: 0,
    // paddingBottom: 20,
    backgroundColor: "white",
    overflow: "hidden",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 5,
  },
});

export default AnimatedBottomSheet;
